import React, { FC, useState, useEffect, useMemo, useCallback } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import {
  ILink_KnowledgeBase_Source,
} from '@kaidam/fc-shared/src/types/knowledgebase'
import {
  IRecord_KnowledgeBase,
  IRecord_KnowledgeBase_Page,
} from '@kaidam/fc-shared/src/types/schema/knowledgebase'
import Box from '@mui/material/Box'

import TinyButton from '../widgets/TinyButton'
import UploadedImage from '../widgets/UploadedImage'
import Window from '../dialog/Window'

import knowledgebaseSelectors from '../../store/selectors/knowledgebase'
import knowledgebaseActions from '../../store/modules/knowledgebase'

import ArticleList from './ArticleList'
import PageList from './PageList'

import icons from '../../icons'

const SearchIcon = icons.search

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  sidebar: {
    width: '300px',
    height: '100%',
    flexGrow: 0,
    borderRight: '1px solid #ccc',
    overflowY: 'auto',
  },
  paper: {
    height: '100%',
  },
  content: {
    flexGrow: 1,
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(4),
  },
  button: {
    margin: theme.spacing(1),
  },
  buttonIcon: {
    width: '20px',
    height: '20px',
  },
  typeContainer: {

  },
  typeHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  typeHeaderIcon: {
    marginRight: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  articleList: {

  },
  info: {

  },
  infoLine: {
    marginBottom: theme.spacing(1), 
  }
}))

interface KnowledgebaseArticleChooserProps {
  value?: ILink_KnowledgeBase_Source,
  onChange: {
    (value: ILink_KnowledgeBase_Source): void,
  },
}

const KnowledgebaseArticleChooser: FC<React.PropsWithChildren<KnowledgebaseArticleChooserProps>> = ({
  value,
  onChange,
}) => {

  const dispatch = useDispatch()
  const classes = useStyles()

  const [ viewingArticle, setViewingArticle ] = useState<IRecord_KnowledgeBase | null>(null)
  const [ open, setOpen ] = useState(false)
  
  const allData = useSelector(knowledgebaseSelectors.allData)
  const typeConfigs = useSelector(knowledgebaseSelectors.typeConfigs)

  const article = useMemo(() => {
    if(!value) return null
    const articles = allData[value.type] || []
    return articles.find(a => a.id === value.id)
  }, [
    allData,
    value,
  ])

  const page = useMemo(() => {
    if(!article) return null
    return article.data.pages.find(p => p.id === value.page)
  }, [
    article,
    value,
  ])

  const screenshot = useMemo(() => {
    if(!article) return ''
    const pageIndex = article.data.pages.findIndex(p => p.id === value.page)
    if(!article.meta?.screenshots) return ''
    return article.meta?.screenshots[pageIndex]
  }, [
    article,
    value,
  ])

  const onPickPage = useCallback((page: IRecord_KnowledgeBase_Page) => {
    onChange({
      project: viewingArticle.project,
      org: viewingArticle.org,
      type: viewingArticle.type,
      id: viewingArticle.id,
      page: page.id,
    })
    setViewingArticle(null)
    setOpen(false)
  }, [
    viewingArticle,
  ])

  useEffect(() => {
    dispatch(knowledgebaseActions.listAll())
  }, [])

  return (
    <Grid container>
      <Grid item xs={12} sm={ 4 }>
        <TinyButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={ () => setOpen(true) }
          className={ classes.button }
        >
          Find Page&nbsp;&nbsp;&nbsp;<SearchIcon className={ classes.buttonIcon } />
        </TinyButton>
      </Grid>
      <Grid item xs={12} sm={ 8 }>
        {
          article && page ? (
            <div className={ classes.info }>
              <div className={ classes.infoLine }>
                <Typography gutterBottom variant="caption">
                  <strong>Article:</strong> { article.name }
                </Typography>
              </div>
              <div className={ classes.infoLine }>
                <Typography gutterBottom variant="caption">
                  <strong>Page:</strong> { page.title }
                </Typography>
              </div>
              {
                screenshot && (
                  <UploadedImage
                    file={ screenshot }
                  />
                )
              }
            </div>
          ) : (
            <Typography variant="caption">
              No article page chosen...
            </Typography>
          )
        }
      </Grid>
      <Window
        open={ open }
        size="md"
        withCancel
        fullHeight
        noScroll
        compact
        cancelTitle="Close"
        onCancel={ () => setOpen(false) }
      >
        <div className={ classes.root }>
          <div className={ classes.sidebar }>
            {
              typeConfigs.map((typeConfig, i) => {
                const articles = allData[typeConfig.name] || []
                return (
                  <React.Fragment key={ i }>
                    <div className={ classes.typeContainer }>
                      <div className={ classes.typeHeader }>
                        <Box color="primary" fontSize={18} textAlign="left">{ typeConfig.title }</Box>
                      </div>
                    </div>
                    <div className={ classes.articleList }>
                      <ArticleList
                        articles={ articles }
                        selected=""
                        onSelect={ setViewingArticle }
                      />
                    </div>
                    {
                      i < typeConfigs.length - 1 && (
                        <Divider className={ classes.divider } />
                      )
                    }
                  </React.Fragment>
                )
              })
            }
          </div>
          <div className={ classes.content }>
            {
              viewingArticle ? (
                <PageList
                  article={ viewingArticle }
                  onSelect={ onPickPage }
                />
              ) : (
                <Typography variant="caption">
                  choose an article...
                </Typography>
              )
            }
          </div>
        </div>
      </Window>
    </Grid>
  )
}

export default KnowledgebaseArticleChooser